import { z } from 'zod';
import { api, request } from './api';
import {
  apSummarySchema,
  catalogItemSchema,
  documentProvidersSchema,
  extractionSchema,
  inventoryComparisonSchema,
  inventoryDocumentDetailSchema,
  inventoryDocumentSchema,
  operationalAccessSchema,
  operationalDocumentDetailSchema,
  operationalDocumentSchema,
  operationalOverviewSchema,
  payableSchema,
  paymentSchema,
  referenceSchema,
  supplierBalanceSchema,
  supplierDetailSchema,
  supplierSchema,
  userModuleGrantsSchema,
  type AccessLevel,
  type OperationalModule,
} from '@/types/operational';

const BASE = '/operational';

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

/** Owner-only, except `me`. Every call is re-checked on the server. */
export const operationalAccessService = {
  /** The caller's own effective access. Drives navigation, nothing more. */
  me: (signal?: AbortSignal) =>
    api.get(`${BASE}/access/me`, operationalAccessSchema, signal),

  read: (userId: string, signal?: AbortSignal) =>
    api.get(`${BASE}/access/users/${userId}`, userModuleGrantsSchema, signal),

  set: (userId: string, grants: { module: OperationalModule; access: AccessLevel }[]) =>
    request(`${BASE}/access/users/${userId}`, userModuleGrantsSchema, {
      method: 'PUT',
      body: { grants },
    }),

  revoke: (userId: string) =>
    api.post(`${BASE}/access/users/${userId}/revoke`, userModuleGrantsSchema),
};

export const operationalOverviewService = {
  read: (businessUnitId?: string, signal?: AbortSignal) =>
    api.get(
      `${BASE}/overview${query({ business_unit_id: businessUnitId })}`,
      operationalOverviewSchema,
      signal,
    ),
};

export const inventoryService = {
  reference: (signal?: AbortSignal) =>
    api.get(`${BASE}/inventory/reference`, referenceSchema, signal),

  catalog: (search = '', signal?: AbortSignal) =>
    api.get(
      `${BASE}/inventory/catalog${query({ search })}`,
      z.array(catalogItemSchema),
      signal,
    ),

  documents: (
    params: { business_unit_id?: string; zone?: string; status?: string } = {},
    signal?: AbortSignal,
  ) =>
    api.get(
      `${BASE}/inventory/documents${query(params)}`,
      z.array(inventoryDocumentSchema),
      signal,
    ),

  document: (id: string, signal?: AbortSignal) =>
    api.get(`${BASE}/inventory/documents/${id}`, inventoryDocumentDetailSchema, signal),

  /**
   * Upload a stock count.
   *
   * Produces a draft and a list of findings — nothing is recorded here, and
   * the caller must go on to `confirm` for the count to become history.
   */
  upload: (input: {
    file: File;
    businessUnitId: string;
    zone: string;
    inventoryDate: string;
    note?: string;
  }) => {
    const form = new FormData();
    form.append('file', input.file);
    form.append('business_unit_id', input.businessUnitId);
    form.append('zone', input.zone);
    form.append('inventory_date', input.inventoryDate);
    if (input.note) form.append('note', input.note);
    return api.upload(
      `${BASE}/inventory/documents/upload`,
      inventoryDocumentDetailSchema,
      form,
    );
  },

  /**
   * Accept a count.
   *
   * Both flags are explicit on purpose: adding articles to the catalogue and
   * confirming past a warning are decisions, and the person has just read the
   * lists they are agreeing to.
   */
  confirm: (id: string, options: { createNewItems: boolean; acknowledgeWarnings: boolean }) =>
    api.post(`${BASE}/inventory/documents/${id}/confirm`, inventoryDocumentDetailSchema, {
      create_new_items: options.createNewItems,
      acknowledge_warnings: options.acknowledgeWarnings,
    }),

  /** Open a correction of a confirmed count. The original is never edited. */
  correction: (id: string, reason: string) =>
    api.post(`${BASE}/inventory/documents/${id}/correction`, inventoryDocumentDetailSchema, {
      reason,
    }),

  comparison: (id: string, signal?: AbortSignal) =>
    api.get(
      `${BASE}/inventory/documents/${id}/comparison`,
      inventoryComparisonSchema,
      signal,
    ),
};

export const suppliersService = {
  list: (search = '', signal?: AbortSignal) =>
    api.get(`${BASE}/suppliers${query({ search })}`, z.array(supplierSchema), signal),

  detail: (id: string, months = 6, signal?: AbortSignal) =>
    api.get(`${BASE}/suppliers/${id}${query({ months })}`, supplierDetailSchema, signal),

  create: (input: {
    name: string;
    legal_name?: string;
    tax_id?: string;
    phone?: string;
    email?: string;
    notes?: string;
  }) => api.post(`${BASE}/suppliers`, supplierSchema, input),

  update: (id: string, input: Record<string, unknown>) =>
    api.patch(`${BASE}/suppliers/${id}`, supplierSchema, input),
};

export const apService = {
  summary: (businessUnitId?: string, signal?: AbortSignal) =>
    api.get(
      `${BASE}/ap/summary${query({ business_unit_id: businessUnitId })}`,
      apSummarySchema,
      signal,
    ),

  balances: (signal?: AbortSignal) =>
    api.get(`${BASE}/ap/balances`, z.array(supplierBalanceSchema), signal),

  payables: (
    params: {
      supplier_id?: string;
      business_unit_id?: string;
      only_open?: boolean;
      overdue?: boolean;
    } = {},
    signal?: AbortSignal,
  ) => api.get(`${BASE}/ap/payables${query(params)}`, z.array(payableSchema), signal),

  createPayable: (input: {
    supplier_id: string;
    business_unit_id: string;
    document_date: string;
    amount: string;
    currency: string;
    document_number?: string;
    due_date?: string;
    description?: string;
  }) => api.post(`${BASE}/ap/payables`, payableSchema, input),

  payments: (params: { supplier_id?: string } = {}, signal?: AbortSignal) =>
    api.get(`${BASE}/ap/payments${query(params)}`, z.array(paymentSchema), signal),

  createPayment: (input: {
    supplier_id: string;
    business_unit_id: string;
    payment_date: string;
    amount: string;
    currency: string;
    reference?: string;
    allocations?: { invoice_id: string; amount: string }[];
    auto_allocate?: boolean;
  }) => api.post(`${BASE}/ap/payments`, paymentSchema, input),

  allocate: (paymentId: string, allocations: { invoice_id: string; amount: string }[]) =>
    api.post(`${BASE}/ap/payments/${paymentId}/allocate`, paymentSchema, { allocations }),

  overdue: (signal?: AbortSignal) =>
    api.get(`${BASE}/ap/overdue`, z.array(payableSchema), signal),

  upcoming: (days = 7, signal?: AbortSignal) =>
    api.get(`${BASE}/ap/upcoming${query({ days })}`, z.array(payableSchema), signal),
};

export const documentsService = {
  /** What this installation can do — names only, never a credential. */
  providers: (signal?: AbortSignal) =>
    api.get(`${BASE}/documents/providers`, documentProvidersSchema, signal),

  list: (
    params: { business_unit_id?: string; supplier_id?: string; document_type?: string } = {},
    signal?: AbortSignal,
  ) =>
    api.get(`${BASE}/documents${query(params)}`, z.array(operationalDocumentSchema), signal),

  detail: (id: string, signal?: AbortSignal) =>
    api.get(`${BASE}/documents/${id}`, operationalDocumentDetailSchema, signal),

  upload: (input: {
    file: File;
    businessUnitId: string;
    documentType: string;
    supplierId?: string;
    note?: string;
  }) => {
    const form = new FormData();
    form.append('file', input.file);
    form.append('business_unit_id', input.businessUnitId);
    form.append('document_type', input.documentType);
    if (input.supplierId) form.append('supplier_id', input.supplierId);
    if (input.note) form.append('note', input.note);
    return api.upload(`${BASE}/documents`, operationalDocumentDetailSchema, form);
  },

  ocr: (id: string) =>
    api.post(`${BASE}/documents/${id}/ocr`, operationalDocumentDetailSchema),

  translate: (id: string) =>
    api.post(`${BASE}/documents/${id}/translate`, operationalDocumentDetailSchema),

  extract: (id: string) =>
    api.post(`${BASE}/documents/${id}/extract`, extractionSchema),

  confirmExtraction: (
    extractionId: string,
    input: {
      supplier_id: string;
      document_date: string;
      amount: string;
      currency: string;
      document_number?: string;
      due_date?: string;
      description?: string;
    },
  ) => api.post(`${BASE}/documents/extractions/${extractionId}/confirm`, extractionSchema, input),

  rejectExtraction: (extractionId: string) =>
    api.post(`${BASE}/documents/extractions/${extractionId}/reject`, extractionSchema),

  /**
   * The original file, always through the authorized API endpoint.
   *
   * Deliberately not an object-storage URL: such a link carries its own
   * authority, outlives the session that produced it, and cannot be revoked
   * when somebody's access is withdrawn.
   */
  fileUrl: (id: string) =>
    `${import.meta.env.VITE_API_URL ?? '/api/v1'}${BASE}/documents/${id}/file`,
};
